import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";
import { useLang } from "../i18n";

export default function ArticoloDettaglio() {
  const { slug } = useParams();
  const { lang, l, data, t } = useLang();
  const { ARTICLES, SITE } = data;
  const p = t.article;
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <Navigate to={l("/guida")} replace />;
  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    inLanguage: lang,
    url: `${SITE.url}${lang === "en" ? "/en" : ""}/guida/${article.slug}`,
    author: { "@type": "Organization", name: SITE.name, url: SITE.url },
    publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
  };

  return (
    <>
      <Seo title={article.title} description={article.excerpt} path={`/guida/${article.slug}`} jsonLd={jsonLd} />
      <article className="max-w-3xl mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-20 md:pb-28">
        <Reveal>
          <Link
            to={l("/guida")}
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-body hover:text-terra transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> {p.back}
          </Link>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight text-ink mt-8">
            {article.title}
          </h1>
          <p className="mt-5 flex items-center gap-3 text-xs text-body">
            <span>{article.date}</span>
            <span className="w-1 h-1 rounded-full bg-sand" aria-hidden="true" />
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" /> {article.readTime} {p.minRead}
            </span>
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="img-frame aspect-[16/9] mt-10">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <div className="mt-12">
          {article.content.map((block, i) =>
            block.type === "h2" ? (
              <Reveal key={i} as="h2">
                <span className="block font-serif text-2xl md:text-3xl text-ink mt-12 mb-2">{block.text}</span>
              </Reveal>
            ) : (
              <Reveal key={i}>
                <p className="text-base md:text-lg text-body leading-relaxed mt-5">{block.text}</p>
              </Reveal>
            )
          )}
        </div>
        <Reveal>
          <div className="mt-16 border-t border-sand pt-10">
            <p className="eyebrow mb-6">{p.continueReading}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {others.map((a) => (
                <Link
                  key={a.slug}
                  to={l(`/guida/${a.slug}`)}
                  data-testid={`related-article-${a.slug}`}
                  className="group border border-sand bg-surface p-6 transition-[transform,box-shadow] duration-500 hover:-translate-y-1"
                >
                  <p className="font-serif text-xl text-ink leading-snug group-hover:text-sage transition-colors duration-300">
                    {a.title}
                  </p>
                  <p className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-sage">
                    {p.read} <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </article>
      <CTABand title={p.ctaTitle} text={p.ctaText} />
    </>
  );
}
